export interface ApiFailure {
  error: { code: string; message: string; requestId: string; details?: Record<string, unknown> };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function cookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

function request(path: string, init: RequestInit = {}): Promise<Response> {
  const method = init.method?.toUpperCase() ?? 'GET';
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = cookie('bpl_csrf');
    if (csrf) headers.set('x-csrf-token', decodeURIComponent(csrf));
  }
  return fetch(path, { ...init, method, headers, credentials: 'include' });
}

async function readPayload<T>(response: Response): Promise<T | ApiFailure | undefined> {
  try {
    return (await response.json()) as T | ApiFailure;
  } catch {
    // Reverse proxies and maintenance pages may return HTML. Keep that detail out of the UI.
    return undefined;
  }
}

let refreshPromise: Promise<boolean> | undefined;

async function refreshWebSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch('/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = undefined;
      });
  }
  return refreshPromise;
}

function canRefresh(response: Response, payload: ApiFailure | undefined): boolean {
  if (response.status !== 401) return false;
  return (
    payload?.error?.code === 'auth_expired' ||
    (payload?.error?.code === 'auth_required' &&
      ['Sign in is required', 'A refresh token is required'].includes(payload.error.message))
  );
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response = await request(path, init);
  let payload = await readPayload<T>(response);
  if (canRefresh(response, payload as ApiFailure | undefined) && (await refreshWebSession())) {
    response = await request(path, init);
    payload = await readPayload<T>(response);
  }
  if (response.status === 204) return undefined as T;
  if (!response.ok) {
    const failure = payload as ApiFailure | undefined;
    throw new ApiError(
      response.status,
      failure?.error?.code ?? 'service_unavailable',
      failure?.error?.message ??
        (response.status >= 500
          ? 'The service is temporarily unavailable. Please try again.'
          : 'The request could not be completed.'),
      failure?.error?.requestId,
    );
  }
  if (payload === undefined)
    throw new ApiError(
      response.status,
      'invalid_response',
      'The service returned an invalid response. Please try again.',
    );
  return payload as T;
}

export async function apiDownload(
  path: string,
  fallbackFilename = 'bikepacking-trip.gpx',
  accept = 'application/gpx+xml',
): Promise<{ blob: Blob; filename: string }> {
  const download = () => fetch(path, { credentials: 'include', headers: { accept } });
  let response = await download();
  let failure: ApiFailure | undefined;
  if (!response.ok) {
    failure = (await readPayload<ApiFailure>(response)) as ApiFailure | undefined;
    if (canRefresh(response, failure) && (await refreshWebSession())) {
      response = await download();
      failure = response.ok
        ? undefined
        : ((await readPayload<ApiFailure>(response)) as ApiFailure | undefined);
    }
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      failure?.error?.code ?? 'unknown_error',
      failure?.error?.message ?? 'The download could not be prepared',
      failure?.error?.requestId,
    );
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/i.exec(disposition)?.[1] ?? fallbackFilename;
  return { blob: await response.blob(), filename };
}

export function idempotencyKey(prefix: string): string {
  return `${prefix}:${crypto.randomUUID()}`;
}
